import { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Table, Button, Badge, Spinner, Alert } from 'react-bootstrap'
import client from '../../api/client'
import ClaudeAccountForm from './ClaudeAccountForm'
import AuthCodeModal from '../../components/AuthCodeModal'

function ClaudeAccounts() {
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingAccount, setEditingAccount] = useState(null)
  const [testingAccount, setTestingAccount] = useState(null)
  const [authAccount, setAuthAccount] = useState(null)
  const [formData, setFormData] = useState({
    email: '',
    label: '',
    plan: 'free',
    daily_request_limit: 500,
    minute_request_limit: 30,
    is_active: true
  })

  useEffect(() => {
    fetchAccounts()
  }, [])

  const fetchAccounts = async () => {
    setLoading(true)
    try {
      const response = await client.get('/admin/api/v1/claude-accounts/')
      setAccounts(response.data || [])
    } catch (err) {
      console.error('Error fetching Claude accounts:', err)
      setError('Erreur lors du chargement des comptes Claude')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingAccount(null)
    setFormData({
      email: '',
      label: '',
      plan: 'free',
      daily_request_limit: 500,
      minute_request_limit: 30,
      is_active: true
    })
    setShowForm(true)
  }

  const handleEdit = (account) => {
    setEditingAccount(account)
    setFormData({
      email: account.email,
      label: account.label,
      plan: account.plan,
      daily_request_limit: account.daily_request_limit,
      minute_request_limit: account.minute_request_limit,
      is_active: account.is_active
    })
    setShowForm(true)
  }

  const handleDelete = async (account) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le compte "${account.email}" ?`)) {
      return
    }

    try {
      await client.delete(`/admin/api/v1/claude-accounts/${account.id}`)
      setSuccess('Compte supprimé avec succès')
      fetchAccounts()
    } catch (err) {
      console.error('Error deleting account:', err)
      setError('Erreur lors de la suppression du compte')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    try {
      if (editingAccount) {
        await client.patch(`/admin/api/v1/claude-accounts/${editingAccount.id}`, formData)
        setSuccess('Compte mis à jour avec succès')
      } else {
        await client.post('/admin/api/v1/claude-accounts/', formData)
        setSuccess('Compte créé avec succès')
      }
      setShowForm(false)
      fetchAccounts()
    } catch (err) {
      console.error('Error saving account:', err)
      setError(err.response?.data?.detail || 'Erreur lors de l\'enregistrement du compte')
    }
  }

  const handleTestConnection = async (account) => {
    setTestingAccount(account.id)
    setError('')
    setSuccess('')

    try {
      const resp = await client.post(`/admin/api/v1/claude-accounts/${account.id}/test`)
      if (resp.data.success) {
        setSuccess(`Connexion testée avec succès pour ${account.email}`)
      } else {
        setError(resp.data.message || `Échec du test de connexion pour ${account.email}`)
      }
    } catch (err) {
      console.error('Error testing connection:', err)
      setError(err.response?.data?.detail || `Échec du test de connexion pour ${account.email}`)
    } finally {
      setTestingAccount(null)
    }
  }

  const getPlanBadge = (plan) => {
    const badges = {
      free: 'secondary',
      pro: 'primary',
      max: 'warning'
    }
    return <Badge bg={badges[plan] || 'secondary'}>{plan.toUpperCase()}</Badge>
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2>Comptes Claude</h2>
          <p className="text-muted">Gérer les comptes Claude (Anthropic) pour l'accès à l'API</p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={handleCreate}>
            + Ajouter un Compte
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Label</th>
                  <th>Plan</th>
                  <th>Usage Quotidien</th>
                  <th>Usage Minute</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">
                      Aucun compte Claude configuré
                    </td>
                  </tr>
                ) : (
                  accounts.map((account) => (
                    <tr key={account.id}>
                      <td>{account.email}</td>
                      <td>{account.label}</td>
                      <td>{getPlanBadge(account.plan)}</td>
                      <td>
                        {account.daily_requests_used} / {account.daily_request_limit}
                      </td>
                      <td>
                        <small className="text-muted">Limite: {account.minute_request_limit}/min</small>
                      </td>
                      <td>
                        <Badge bg={account.is_active ? 'success' : 'danger'}>
                          {account.is_active ? 'Actif' : 'Inactif'}
                        </Badge>
                        {account.needs_reauth && (
                          <Badge bg="warning" className="ms-1">Auth requise</Badge>
                        )}
                      </td>
                      <td>
                        <Button
                          variant={account.needs_reauth ? 'warning' : 'outline-warning'}
                          size="sm"
                          className="me-2"
                          onClick={() => setAuthAccount(account)}
                        >
                          Authentifier
                        </Button>
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="me-2"
                          onClick={() => handleTestConnection(account)}
                          disabled={testingAccount === account.id}
                        >
                          {testingAccount === account.id ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-1" />
                              Test...
                            </>
                          ) : (
                            'Tester'
                          )}
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleEdit(account)}
                        >
                          Modifier
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(account)}
                        >
                          Supprimer
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <ClaudeAccountForm
        show={showForm}
        onHide={() => setShowForm(false)}
        editingAccount={editingAccount}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
      />

      <AuthCodeModal
        show={!!authAccount}
        onHide={() => setAuthAccount(null)}
        account={authAccount}
        apiBasePath="/admin/api/v1/claude-accounts"
        onSuccess={() => {
          setSuccess('Authentification réussie')
          fetchAccounts()
        }}
      />
    </Container>
  )
}

export default ClaudeAccounts
